<template>
  <AtomsContainersCornerBorders v-if="dataObject" class="flex-1 min-w-[260px]">
    <div ref="element">
      <div class="performance-meter">
        <strong class="performance-meter__name">
          {{ dataObject.name }}
        </strong>
        <div v-if="dataObject.typeName && dataObject.typeValue" class="performance-meter__type">
          <span class="performance-meter__type__name">
            {{ dataObject.typeName }}
          </span>
          <strong class="performance-meter__type__value">
            {{ dataObject.typeValue }}
          </strong>
        </div>
        <div class="performance-meter__gauge">
          <div class="performance-meter__gauge__wrapper">
            <div class="performance-meter__gauge__circle">
              <div class="performance-meter__gauge__mask" style="transform: rotate(90deg)">
                <div class="performance-meter__gauge__fill" :style="gaugeStyle"></div>
              </div>
              <div class="performance-meter__gauge__value">
                <div class="performance-meter__rating">
                  {{ dataObject.rating }}/10
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="performance-meter__label">{{ $t('celkové hodnocení') }}</div>
        <AtomsTextLink v-if="dataObject.link?.href" :href="dataObject.link.href" class="performance-meter__link">{{ dataObject.link.text }}</AtomsTextLink>
        <template v-for="valueItem in dataObject.values">
          <span class="performance-meter__value">
            {{ valueItem.name }}
          </span>
          <div class="performance-meter__bar" :class="{ 'text-neutral-50': valueItem.rating >= 8 }">
            {{ valueItem.description }} ({{ valueItem.rating }}/10)
            <div class="performance-meter__bar__rating" :style="barStyle(valueItem.rating)"></div>
          </div>
        </template>
      </div>
    </div>
  </AtomsContainersCornerBorders>
</template>

<script setup>

const props = defineProps({
  data: String
});

const colors = {
  0: "#EF4444",
  1: "#EF4444",
  2: "#EF4444",
  3: "#EF4444",
  4: "#EF4444",
  5: "#FBBF24",
  6: "#FBBF24",
  7: "#FBBF24",
  8: "#10B981",
  9: "#10B981",
  10: "#10B981"
};

const dataObject = computed(() => {

  let result;

  try {
    if (props.data) {
      result = JSON.parse(props.data.replace(/&quot;/g, '"'));
    }
  } catch (e) {
    console.log(e);
  }

  return result;

});

const getCssTransform = (rating, offset = 0) => `rotate(${18 * rating + offset}deg)`;
const getCssAnimation = (rating) => `gauge${rating} ease-in-out 1.5s`;

const gaugeStyle = computed(() => {
  if (!isVisible.value) return '';
  return `transform: ${getCssTransform(dataObject.value.rating)};
              animation: ${getCssAnimation(dataObject.value.rating)};
              background-color: ${colors[dataObject.value.rating]}`;
});

const barStyle = computed(() => {
  return (rating) => {
    if (!isVisible.value) return '';
    return `width: ${rating * 10}%;
                animation: progress${rating} ease-in-out 1.5s;
                background-color: ${colors[rating]}`;
  };
});

const observer = ref(null);
const isVisible = ref(false);
const element = ref(null);

onMounted(() => {
  if (element.value) {
    observer.value = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true;
        observer.value.disconnect();
      }
    }, { threshold: 0.8 });

    observer.value.observe(element.value);
  }
});

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});

</script>

<style lang="postcss">
.performance-meter {
  padding: 30px;
  text-align: center;
  font-size: 1.2em;
  background-color: white;
  @apply tracking-normal;

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
  }

  .info-content.magazine &__wrapper {
    margin-left: -80px;
    margin-right: -80px;
  }

  &__name {
    display: block;
    margin: 10px 0;
    font-size: 1.3em;
  }

  &__type {
    @apply my-6;
    text-align: center;
    font-size: 0.8em;
  }

  &__rating {
    margin-top: -2.25rem;
    font-weight: bold;
    @apply text-4xl;
  }

  &__label {
    display: block;
    color: gray;
    margin-bottom: 5px;
    font-weight: normal;
    @apply text-sm;
  }

  &__value {
    display: block;
    text-align: left;
    margin-bottom: 5px;
    @apply text-sm;
  }

  &__bar {
    position: relative;
    z-index: 2;
    padding: 5px 10px;
    margin-bottom: 15px;
    text-align: left;
    font-weight: bold;
    font-size: 0.8em;
    @apply border border-neutral-200 bg-neutral-100;

    &__rating {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      z-index: -1;
    }
  }

  &__gauge {
    margin: 20px auto;
    width: 200px;
    height: 100px;
    overflow: hidden;
    @apply mb-2;

    &__wrapper {
      transform: rotate(180deg);
      transform-origin: 100px 100px;
      border-radius: 50%;
      width: 200px;
      height: 200px;
      @apply bg-neutral-100;
    }

    &__mask,
    &__fill {
      width: 200px;
      height: 200px;
      position: absolute;
      border-radius: 50%;
    }

    &__mask {
      clip: rect(0px, 200px, 200px, 100px);
    }

    &__mask--full,
    &__fill {
      /*transform: rotate(90deg);*/
    }

    &__fill {
      clip: rect(0px, 100px, 200px, 0px);
    }

    &__value {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 160px;
      height: 160px;
      border-radius: 50%;
      background: #fff;
      text-align: center;
      margin-top: 20px;
      margin-left: 20px;
      position: absolute;
      z-index: 100;
      font-weight: 700;
      transform: rotate(-180deg);
    }
  }

  &__link {
    display: inline-block;
    min-height: 1em;
    line-height: 1em;
    margin-top: 10px;
    margin-bottom: 10px;
    font-size: 1rem;
    text-decoration: underline;
    @apply text-primary-500;
  }
}

@keyframes gauge1 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(18deg);
  }
}

@keyframes gauge2 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(36deg);
  }
}

@keyframes gauge3 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(54deg);
  }
}

@keyframes gauge4 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(72deg);
  }
}

@keyframes gauge5 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(90deg);
  }
}

@keyframes gauge6 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(108deg);
  }
}

@keyframes gauge7 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(126deg);
  }
}

@keyframes gauge8 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(120deg);
  }
}

@keyframes gauge9 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(162deg);
  }
}

@keyframes gauge10 {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(180deg);
  }
}

@keyframes progress1 {
  0% {
    width: 0;
  }

  100% {
    width: 10%;
  }
}

@keyframes progress2 {
  0% {
    width: 0;
  }

  100% {
    width: 20%;
  }
}

@keyframes progress3 {
  0% {
    width: 0;
  }

  100% {
    width: 30%;
  }
}

@keyframes progress4 {
  0% {
    width: 0;
  }

  100% {
    width: 40%;
  }
}

@keyframes progress5 {
  0% {
    width: 0;
  }

  100% {
    width: 50%;
  }
}

@keyframes progress6 {
  0% {
    width: 0;
  }

  100% {
    width: 60%;
  }
}

@keyframes progress7 {
  0% {
    width: 0;
  }

  100% {
    width: 70%;
  }
}

@keyframes progress8 {
  0% {
    width: 0;
  }

  100% {
    width: 80%;
  }
}

@keyframes progress9 {
  0% {
    width: 0;
  }

  100% {
    width: 90%;
  }
}

@keyframes progress10 {
  0% {
    width: 0;
  }

  100% {
    width: 100%;
  }
}
</style>